import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MUIDataTable from 'mui-datatables';
import {
  Icon,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { TextFormattedNumber, BackdropCircular } from 'components';
import { updateQuoteProListingApi } from 'api';
import EditDiscount from './EditDiscount';
import { calculateDiscountValueField } from './helpers/quoteDiscounts';


const useStyles = makeStyles(() => ({
  root: {
    border: '1px dashed #C0C0CF',
    borderRadius: '4px',
    '& .MuiTableCell-root': {
      color: '#32324D',
      borderBottom: '1px solid #F6F6F9'
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      fontSize: '11px',
      textTransform: 'uppercase',
      color: '#666687',
    }
  }
}));

const QuoteDiscountsTable = ({quoteDiscounts}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { editableQuoteProduct, savingProductLoading } = useSelector((state: IReduxState) => state.quote);

  const [openEditDiscount, setOpenEditDiscount] = useState(false);
  const [editableDiscountData, setEditableDiscountData] = useState(null);

  const handleDeleteDiscount = (quoteDiscounts) => {
    const {price_type, price} = quoteDiscounts;

    const { custom_attributes } = editableQuoteProduct;
    const existingQuoteproDiscounts = custom_attributes.find((attr) => attr.attribute_code === 'quotepro_discounts');

    if (existingQuoteproDiscounts) {
      const existingValues = JSON.parse(existingQuoteproDiscounts.value);
      const updatedValues = existingValues.filter((i) => !(i.price_type === price_type && i.price === price));

      const updatedStringifiedValues = JSON.stringify(updatedValues);

      const editedProduct = {
        ...editableQuoteProduct,
        custom_attributes: [
          ...(custom_attributes.filter((attr) => attr.attribute_code !== 'quotepro_discounts')),
          {
            attribute_code: 'quotepro_discounts',
            value: updatedStringifiedValues
          }
        ]
      };

      updateQuoteProListingApi({ dispatch, product: editedProduct });
    }
  };

  const columns = [
    {
      name: 'title',
      label: 'Type',
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const { price_type } = quoteDiscounts[dataIndex];
          return (
            <span className="font-bold">{price_type == 'percent' ? 'Percentage Off' : 'Dollar Off'}</span>
          );
        }
      }
    },
    {
      name: 'discount',
      label: 'Discount',
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const {price_type, price} = quoteDiscounts[dataIndex];
          let decimalScale = 2;

          if (price_type === 'percent') {
            decimalScale = String(price).split('.')?.[1]?.length || 0;
          }

          return (
            <TextFormattedNumber
              value={Math.abs(price)}
              suffix={price_type === 'percent' ? '%' : ''}
              prefix={price_type === 'fixed' ? '$' : ''}
              decimalScale={decimalScale}
            />
          );
        }
      }
    },
    {
      name: 'discount-value',
      label: 'Discount Value',
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const { price, price_type } = quoteDiscounts[dataIndex];
          return (
            <span>{calculateDiscountValueField(price_type, editableQuoteProduct?.price, price)}</span>
          );
        }
      }
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        sort: false,
        setCellHeaderProps: () => ({style: {width: '150px'}}),
        customBodyRenderLite: (dataIndex) => {
          return (
            <>
              <IconButton
                className="mr-3 p-2"
                title="Edit"
                size="small"
                onClick={() => {
                  setEditableDiscountData(quoteDiscounts[dataIndex]);
                  setOpenEditDiscount(true);
                }}
              >
                <Icon fontSize="small">edit</Icon>
              </IconButton>
              <IconButton
                className="mr-3 p-2"
                title="Delete"
                size="small"
                onClick={() => handleDeleteDiscount(quoteDiscounts[dataIndex])}
              >
                <Icon fontSize="small">delete</Icon>
              </IconButton>
            </>
          );
        }
      }
    }
  ];
  return (
    <>
      <MUIDataTable
        className={classes.root}
        data={quoteDiscounts}
        columns={columns}
        options={{
          responsive: 'vertical',
          selectableRows: 'none',
          search: false,
          filter: false,
          download: false,
          print: false,
          pagination: false,
          viewColumns: false,
          elevation: 0
        }}
      />
      <EditDiscount
        open={openEditDiscount}
        onClose={setOpenEditDiscount}
        editableOption={editableDiscountData}
      />
      <BackdropCircular loading={savingProductLoading} />
    </>
  );
};

export default QuoteDiscountsTable;
